import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {pluralize} from 'BenchlingUtil';
import {SampleDetails} from 'constants/SampleConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {SampleDescriptor} from 'notebook/sample-propagation/util/getSampleDescriptor';
import getSampleDetailsName from 'notebook/sample-propagation/util/getSampleDetailsName';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import {BLUE, RED_LIGHT} from 'ui-library/Colors';
import usePrevious from 'ui-library/hooks/usePrevious';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import SetAllDropdownWrapper from 'ui-library/SetAllDropdownWrapper';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as keyboard from 'util/Keyboard';
import removeNulls from 'util/removeNulls';
import stopPropagation from 'util/stopPropagation';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

import {SampleWithRowCount} from '../AddSamplesFromWorklistModal';
import dedupeSampleDetails from '../util/dedupeSampleDetails';
import parseRowCount from './parseRowCount';
import {RowCountDetails} from './RowCountDetails';
import useRowSelectionState from './useRowSelectionState';
import useSampleQueryData from './useSampleQueryData';
import useSampleRowCounts from './useSampleRowCounts';

function BulkSetRowCountDropdown(props: {
  minRowCount: number;
  maxRowCount: number;
  bulkSetRowCountText(newRowCountText: string): void;
}): JSX.Element {
  const [newRowCountText, setNewRowCountText] = useState('');
  const {isValid} = parseRowCount(newRowCountText, props.minRowCount, props.maxRowCount);

  return (
    <SetAllDropdownWrapper
      isDisabled={!isValid}
      onSetAll={() => {
        props.bulkSetRowCountText(newRowCountText);
      }}
      icon="bulk-edit"
    >
      {({setAll, close}) => (
        <FormInput
          className={classNames(!isValid && 'fieldInput-input--error')}
          focusOnMount={true}
          onChange={(e: InputChangeEvent) => {
            setNewRowCountText(e.target.value);
          }}
          title="Replicate all"
          value={newRowCountText}
          onKeyDown={(e: ElementKeyboardEvent) => {
            if (keyboard.isEnterKey(e) && isValid) {
              setAll();
            } else if (keyboard.isEscapeKey(e)) {
              close();
            }
          }}
        />
      )}
    </SetAllDropdownWrapper>
  );
}

interface SampleRow {
  sampleDetails: SampleDetails;
  isSelected: boolean;
  rowCountDetails: RowCountDetails;
}

export interface SourceSampleSelectionTableProps {
  // Unique identifier of the source of the samples.
  sourceKey: string;
  samples: Array<SampleDetails>;
  sampleDescriptor: SampleDescriptor;
  onChangeSelection: (
    // Null if any of the row counts are invalid.
    samplesWithRowCounts: Array<SampleWithRowCount> | null
  ) => void;
  sourceName?: string;
  removeDuplicates?: boolean;
}

const MIN_NEW_ROW_COUNT = 0;
const DEFAULT_ROW_COUNT = 1;
const MAX_NEW_ROW_COUNT = 500;

/**
 * Reset all row states if the samples change, or if the source of samples changes.
 * @param sourceKey Unique identifier of the source of the samples
 * @param samples The current list of samples
 * @param resetRowStates Callback to reset all row states
 */
function useResetOnSampleOrSourceChange(
  sourceKey: string,
  samples: Array<SampleDetails | null> | null,
  resetRowStates: () => void
): void {
  // If the source of samples changes, reset the row state for all samples.
  // Why? We are assuming that the user thinks of the row states as being for a particular source.
  // If the source changes, the row's state is no longer relevant,
  // even if the sample happens to be present in both the previous and the new source.

  // Just to be safe, also reset row states when samples are loaded, or when the number of samples changes.
  const numSamples: number | null = samples?.length ?? null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(resetRowStates, [sourceKey, numSamples]);
}

function getSelectedSamplesWithRowCounts(
  samples: Array<SampleDetails | null> | null,
  isRowSelected: ReadonlyArray<boolean>,
  rowCountDetailsByIndex: Array<RowCountDetails>
): Array<SampleWithRowCount> | null {
  if (samples == null) {
    return null;
  }
  const samplesWithRowCounts: Array<SampleWithRowCount> = [];

  for (let i = 0; i < samples.length; i++) {
    if (!isRowSelected[i]) {
      continue;
    }

    const {isValid, rowCount} = rowCountDetailsByIndex[i];
    if (!isValid) {
      return null;
    }

    const sample = samples[i];
    if (sample != null) {
      samplesWithRowCounts.push({
        sample,
        rowCount,
      });
    }
  }

  return samplesWithRowCounts;
}

interface MissingSamplesProps {
  archivedIds: Array<string>;
  inaccessibleIds: Array<string>;
  sampleDescriptor: SampleDescriptor;
}

// If any samples were removed (for being archived or inaccessible), let the user know:
//   "12 archived and 3 inaccessible samples were removed"
function MissingSamplesMessage(props: MissingSamplesProps): JSX.Element | null {
  const archivedCount = props.archivedIds.length;
  const inaccessibleCount = props.inaccessibleIds.length;
  const totalCount = archivedCount + inaccessibleCount;

  if (totalCount === 0) {
    return null;
  }
  let msg = '';
  if (archivedCount > 0) {
    msg += `${archivedCount} archived `;
  }
  if (inaccessibleCount > 0) {
    if (archivedCount > 0) {
      msg += 'and ';
    }
    msg += `${inaccessibleCount} inaccessible `;
  }
  msg += totalCount > 1 ? props.sampleDescriptor.plural : props.sampleDescriptor.singular;
  msg += pluralize(archivedCount + inaccessibleCount, ' was', ' were');
  return <p>{msg} removed</p>;
}

export default function SourceSampleSelectionTable(
  props: SourceSampleSelectionTableProps
): JSX.Element | null {
  const removeDuplicates = props.removeDuplicates ?? true;

  const allSamples = removeDuplicates ? dedupeSampleDetails(props.samples) : props.samples;
  const queryData = useSampleQueryData(allSamples);
  const {
    samplesByIndex: samplesByIndexIncludingInaccessible,
    archivedIds,
    inaccessibleIds,
    status,
  } = queryData;
  const samplesByIndex = removeNulls(samplesByIndexIncludingInaccessible);
  const errorSampleIndices = new Set<number>(
    removeNulls(samplesByIndex.map((sample, index) => (sample?.error == null ? null : index)))
  );

  const {
    resetAllSelectionState,
    rowSelectionState: isRowSelected,
    toggleRowSelected: toggleSampleSelected,
    toggleAllRowsSelected: toggleAllSamplesSelected,
  } = useRowSelectionState(samplesByIndex.length ?? 0, errorSampleIndices);
  const {rowCountDetailsByIndex, bulkSetRowCountText, resetSampleRowCounts, setRowCountText} =
    useSampleRowCounts(samplesByIndex.length, {
      minRowCount: MIN_NEW_ROW_COUNT,
      defaultRowCount: DEFAULT_ROW_COUNT,
      maxRowCount: MAX_NEW_ROW_COUNT,
    });

  useResetOnSampleOrSourceChange(props.sourceKey, samplesByIndex, () => {
    resetAllSelectionState();
    resetSampleRowCounts();
  });

  // Notify parent component whenever the selected samples or the row counts change.
  const selectedSamplesWithRowCounts =
    queryData.status === QueryResultType.SUCCESS
      ? getSelectedSamplesWithRowCounts(samplesByIndex, isRowSelected, rowCountDetailsByIndex)
      : null;
  const previousSelectedSamplesWithRowCounts = usePrevious(selectedSamplesWithRowCounts);
  useEffect(() => {
    if (!isEqual(selectedSamplesWithRowCounts, previousSelectedSamplesWithRowCounts)) {
      props.onChangeSelection(selectedSamplesWithRowCounts);
    }
  });

  if (status === QueryResultType.PENDING || samplesByIndex == null) {
    return <CenteredHorizontalThrobber />;
  }
  if (status === QueryResultType.ERROR) {
    return <p>There was a problem loading your samples.</p>;
  }
  if (samplesByIndex.length === 0) {
    if (archivedIds.length > 0 || inaccessibleIds.length > 0) {
      return (
        <MissingSamplesMessage
          archivedIds={archivedIds}
          inaccessibleIds={inaccessibleIds}
          sampleDescriptor={props.sampleDescriptor}
        />
      );
    }
    return null;
  }

  const sampleRows: Array<SampleRow> = samplesByIndex
    .map((sampleDetails, index) => ({
      sampleDetails,
      isSelected: isRowSelected[index],
      rowCountDetails: rowCountDetailsByIndex[index],
    }))
    .filter((x): x is SampleRow => x.sampleDetails != null);

  return (
    <>
      <MissingSamplesMessage
        archivedIds={archivedIds}
        inaccessibleIds={inaccessibleIds}
        sampleDescriptor={props.sampleDescriptor}
      />
      <Table
        isCompact={true}
        className={css(styles.tableContainer)}
        frozenHeader={true}
        overflowVisible={true}
        numRows={sampleRows.length}
        // Do not add a onRowClick handler, because it gets called if the user clicks out of the row count input. See BNCH-120966.
      >
        {/*
         * Column 1 of 3:
         * Checkboxes.
         */}
        <Column
          header={
            <Cell className="simpleTable-checkboxCell">
              <IndeterminateCheckbox
                className={css(styles.checkbox)}
                items={sampleRows}
                disabledMap={(sampleRow) => sampleRow.sampleDetails.error != null}
                selectedMap={(sampleRow) => sampleRow.isSelected}
                onChange={toggleAllSamplesSelected}
              />
            </Cell>
          }
          renderCell={(rowIndex: number) => {
            const sampleRow = sampleRows[rowIndex];
            return (
              <Cell
                className={classNames('simpleTable-checkboxCell', css(styles.checkboxCell))}
                // We add a click handler to the cell in case the user clicks just outside the checkbox bounds.
                onClick={() => {
                  toggleSampleSelected(rowIndex);
                }}
              >
                {sampleRow.sampleDetails.error ? (
                  <div
                    className={css(styles.errorIndicator)}
                    {...getTestProps({
                      component: 'SourceSampleSelectionTable',
                      element: 'errorIndicator',
                    })}
                  />
                ) : null}
                <input
                  type="checkbox"
                  className={css(styles.checkbox)}
                  checked={!sampleRow.sampleDetails.error && sampleRow.isSelected}
                  disabled={sampleRow.sampleDetails.error != null}
                  onChange={() => {
                    toggleSampleSelected(rowIndex);
                  }}
                />
              </Cell>
            );
          }}
        />

        {/*
         * Column 2 of 3:
         * Sample names.
         */}
        <Column
          header={
            <Cell>
              <span className="u-pre-line">{props.sourceName ?? props.sampleDescriptor.title}</span>
            </Cell>
          }
          renderCell={(rowIndex: number) => (
            <Cell
              className={css(styles.sampleCell)}
              onClick={() => {
                toggleSampleSelected(rowIndex);
              }}
            >
              {getSampleDetailsName(sampleRows[rowIndex].sampleDetails)}
            </Cell>
          )}
        />

        {/*
         * Column 3 of 3:
         * Row count inputs.
         */}
        <Column
          header={
            <Cell className={css(styles.rowCountInputCell)}>
              # of replicates
              <span className={css(styles.bulkEditIcon)}>
                <BulkSetRowCountDropdown
                  minRowCount={MIN_NEW_ROW_COUNT}
                  maxRowCount={MAX_NEW_ROW_COUNT}
                  bulkSetRowCountText={bulkSetRowCountText}
                />
              </span>
            </Cell>
          }
          renderCell={(rowIndex: number) => {
            const sampleRow = sampleRows[rowIndex];
            return (
              <Cell
                className={css(styles.rowCountInputCell)}
                // Prevent clicking on the row and toggling the selection state
                onClick={stopPropagation}
              >
                <FormInput
                  className={classNames(!sampleRow.rowCountDetails.isValid && 'fieldInput-input--error')}
                  disabled={!sampleRow.isSelected}
                  focusOnMount={false}
                  onChange={(e: InputChangeEvent) => {
                    setRowCountText(rowIndex, e.target.value);
                  }}
                  value={sampleRow.isSelected ? sampleRow.rowCountDetails.rowCountText : ''}
                />
              </Cell>
            );
          }}
        />
      </Table>
    </>
  );
}

const styles = StyleSheet.create({
  tableContainer: {
    maxHeight: 300,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  bulkEditIcon: {
    float: 'right',
  },
  checkboxCell: {
    position: 'relative',
  },
  sampleCell: {
    width: '50%',
    whiteSpace: 'normal',
  },
  rowCountInputCell: {
    width: '50%',
  },
  checkbox: {
    marginLeft: 6,
    color: BLUE,
  },
  errorIndicator: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 4,
    height: '100%',
    background: RED_LIGHT,
  },
});
